import React from 'react'

// Form
import { Formik, Form, FormikValues, FormikHelpers } from 'formik'

// Hooks
import useFormDuuf from './useFormDuuf'

// Functions
import schema from './schema'
import defaultValues from './defaultValues'

// Fields
import TextField from './fields/TextField'
import TextAreaField from './fields/TextAreaField'
import ConsentField from './fields/ConsentField'
import FileUploadField from './fields/FileUploadField'
import SelectField from './fields/SelectField'
import DateField from './fields/DateField'
import SectionField from './fields/SectionField'
import CheckboxesField from './fields/CheckboxesField'

export const FormContext = React.createContext({})
export const FormProvider = FormContext.Provider
export const FormConsumer = FormContext.Consumer

export interface ContextProps {
  fields: Array<any>
  formik: any
  form: any
  privacyUrl?: string
  privacyUrlPlacement?: string
}

interface FormDuufField {
  [key: string]: string
}

export interface FormDuufFieldData {
  id: number
  type: 'email' | 'textarea' | 'consent'
  labelPlacement: 'hidden_label'
  placeholder: string
  label: string
  isRequired: boolean
  checkboxLabel: string
  choices: string
  visibility: 'hidden' | 'visible'
  allowedExtensions: string
  description: string
  errorMessage?: string
}

interface FormDuufSchema {
  [key: string]: any
}

export interface FormDuufProps {
  id: number
  defaultFields?: FormDuufField
  generate?: boolean
  privacyUrl?: string
  privacyUrlPlacement?: string
  scrollToConfirm?: boolean
  scrollTopOffset?: number
  className?: string
  yupSchema?: FormDuufSchema
  loadingComponent: React.FC
  contentComponent: React.FC<{ content: string; className?: string }>
  graphqlData: any
}

export const FormDuuf: React.FC<FormDuufProps> = ({
  id,
  generate = true,
  scrollToConfirm = true,
  scrollTopOffset = 0,
  className = '',
  children,
  privacyUrl,
  privacyUrlPlacement,
  defaultFields = {},
  loadingComponent: Loading,
  contentComponent: Content,
  graphqlData = { edges: [] },
  yupSchema = {}
}) => {
  const form = graphqlData.edges.find(({ node }: any) => node.formId === id)

  if (!form) {
    return null
  }

  const [confirmMessage, setConfirmMessage] = React.useState<string>('')
  const formRef: React.RefObject<HTMLDivElement> = React.useRef(null)

  const executeScrollToConfirm = () =>
    window.scrollTo({
      top: formRef.current.offsetTop + scrollTopOffset,
      behavior: 'smooth'
    })

  const handleSubmit = async (event: React.FormEvent<HTMLInputElement>, values: FormikValues, actions: FormikHelpers<FormikValues>) => {
    event.preventDefault()

    actions.setSubmitting(true)

    try {
      const formData = new FormData()

      Object.keys(values).forEach((key: string) => {
        formData.append(key, values[key])
      })

      const request = await fetch(`${form.node.apiURL}/submissions`, {
        method: 'POST',
        body: formData
      })

      const response = await request.json()

      if (response.is_valid === true) {
        setConfirmMessage(response.confirmation_message)
        actions.setStatus('done')

        setTimeout(() => {
          if (scrollToConfirm) {
            executeScrollToConfirm()
          }
        }, 100)
      } else {
        actions.setStatus('error')
      }
    } catch (error) {
      actions.setStatus('error')
    }

    actions.setSubmitting(true)
  }

  return (
    <div className={className} ref={formRef}>
      <Formik
        initialValues={{
          ...defaultValues(form.node.formFields),
          ...defaultFields
        }}
        onSubmit={async () => null}
        validationSchema={schema(form.node.formFields, yupSchema)}
      >
        {(formik) => {
          if (formik.status === 'done') {
            return <Content className="form-duuf-confirm" content={confirmMessage} />
          }

          if (formik.status === 'error') {
            return <Content className="form-duuf-something-wrong" content="Er ging iets fout" />
          }

          return (
            <FormProvider value={{ fields: form.node.formFields, formik, form: form.node, privacyUrl, privacyUrlPlacement }}>
              <Form
                id={`form_${id}`}
                onSubmit={(event: React.FormEvent<HTMLInputElement>) => handleSubmit(event, formik.values, formik)}
                method="post"
              >
                {formik.isSubmitting && (
                  <div className="form-duuf-loading">
                    <Loading />
                  </div>
                )}
                {generate ? (
                  <>
                    <FormDuufGroup />
                    <FormDuufSubmit />
                  </>
                ) : (
                  children
                )}
              </Form>
            </FormProvider>
          )
        }}
      </Formik>
    </div>
  )
}

interface FormDuufFieldProps {
  id: number
}

interface FormDuufGroupProps {
  showIds?: Array<number>
  hideIds?: Array<number>
}

export const FormDuufGroup: React.FC<FormDuufGroupProps> = ({ showIds = [], hideIds = [] }) => {
  const context = useFormDuuf()

  const fields = context.fields.filter((field) => {
    if (showIds.length > 0 && showIds.indexOf(field.id) === -1) {
      return false
    }

    if (hideIds.length > 0 && hideIds.indexOf(field.id) !== -1) {
      return false
    }

    return true
  })

  return (
    <>
      {fields
        ?.filter((field) => field?.visibility !== 'hidden')
        .map((field) => (
          <FormDuufField key={field?.id} id={Number(field?.id)} />
        ))}
    </>
  )
}

export const FormDuufField: React.FC<FormDuufFieldProps> = ({ id }) => {
  const context = useFormDuuf()

  const data: FormDuufFieldData = context.fields.find((field) => field.id === id)

  if (!data) {
    return null
  }

  const types: any = {
    number: TextField,
    text: TextField,
    email: TextField,
    phone: TextField,
    textarea: TextAreaField,
    consent: ConsentField,
    select: SelectField,
    fileupload: FileUploadField,
    date: DateField,
    section: SectionField,
    checkbox: CheckboxesField
  }

  if (!types[data.type]) {
    return null
  }

  const Component = types[data.type]

  const fieldId: string = `input_${data.id}`

  const hasError: boolean =
    context.formik.errors && context.formik.errors[fieldId] && context.formik.touched && context.formik.touched[fieldId]

  return <Component hasError={hasError} formik={context.formik} fieldId={fieldId} data={data} />
}

export interface FieldProps {
  fieldId: string
  data: FormDuufFieldData
  formik?: any
  hasError?: boolean
}

export const FormDuufSubmit = () => {
  const context = useFormDuuf()
  const isValid: boolean =
    context.formik.isSubmitting === false &&
    Object.keys(context.formik.touched).length > 0 &&
    Object.keys(context.formik.errors).length === 0

  return (
    <div className="form-duuf-group form-duuf-group-submit">
      <span className={`form-duuf-submit${!isValid ? ' form-duuf-submit-disabled' : ''}`}>
        <button type="submit" disabled={!isValid}>
          {context.form.button.text}
        </button>
      </span>
    </div>
  )
}

export default FormDuuf
